import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, ProgressBar, Button, Form, Modal, Table } from 'react-bootstrap';
import { fetchAllParkingLot } from '../../services/adminService';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import './AdminParkingLot.css';

const AdminParkingLot = () => {
    const [parkingLots, setParkingLots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [selectedLot, setSelectedLot] = useState(null);

    const loadParkingLots = async () => {
        setLoading(true);
        try {
            const res = await fetchAllParkingLot();
            if (res && res.ok && Array.isArray(res.data)) {
                setParkingLots(res.data);
            } else if (Array.isArray(res?.data)) {
                setParkingLots(res.data);
            } else {
                // Fallback structured data if API returns empty array initially
                setParkingLots([
                    { id: 1, parkingLotName: "Bãi xe Khu A (Nhà V)", capacity: 250, currentVehicles: 180, location: "Tòa nhà V - Cổng chính", status: "Active" },
                    { id: 2, parkingLotName: "Bãi xe Khu B (Nhà H)", capacity: 150, currentVehicles: 95, location: "Tòa nhà H - Cổng Phụ", status: "Active" },
                    { id: 3, parkingLotName: "Bãi xe Khu C (Nhà X)", capacity: 200, currentVehicles: 198, location: "Khu học xưởng X", status: "Full" }
                ]);
            }
        } catch (error) {
            console.error('Error fetching parking lots:', error);
            toast.error('Không thể tải danh sách bãi xe');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadParkingLots();
    }, []);

    const filteredLots = parkingLots.filter(lot =>
        (lot.parkingLotName || lot.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lot.location || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalCapacity = parkingLots.reduce((acc, curr) => acc + parseInt(curr.capacity || curr.totalSpot || 0, 10), 0);
    const totalOccupied = parkingLots.reduce((acc, curr) => acc + parseInt(curr.currentVehicles || curr.occupiedSpot || 0, 10), 0);
    const availableSlots = totalCapacity - totalOccupied;

    const handleOpenEdit = (lot) => {
        setSelectedLot(lot);
        setShowModal(true);
    };

    const handleSaveLot = (e) => {
        e.preventDefault();
        toast.success("Cập nhật thông tin bãi đỗ xe thành công!");
        setShowModal(false);
        loadParkingLots();
    };

    return (
        <div className="admin-parking-page py-4">
            <Helmet>
                <title>IUH Parking Admin - Quản Lý Bãi Giữ Xe</title>
            </Helmet>

            <Container fluid className="px-4">
                {/* Header Card */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                    <div>
                        <h3 className="fw-extrabold text-dark mb-1 d-flex align-items-center gap-2">
                            <i className="bi bi-p-square-fill text-primary"></i>
                            Quản Lý Khu Vực Bãi Giữ Xe
                        </h3>
                        <p className="text-secondary small mb-0">
                            Giám sát trạng thái, sức chứa và điều chỉnh vị trí đỗ xe tại bãi đỗ IUH Parking
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <Button variant="outline-secondary" size="sm" className="rounded-pill px-3" onClick={loadParkingLots}>
                            <i className="bi bi-arrow-clockwise me-1"></i> Làm mới
                        </Button>
                        <Button variant="primary" size="sm" className="rounded-pill px-4 fw-bold shadow-sm" onClick={() => handleOpenEdit(null)}>
                            <i className="bi bi-plus-circle-fill me-1"></i> Thêm bãi xe mới
                        </Button>
                    </div>
                </div>

                {/* KPI Overview Cards */}
                <Row className="g-3 mb-4">
                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 bg-white">
                            <Card.Body className="p-4 d-flex justify-content-between align-items-center">
                                <div>
                                    <span className="text-secondary small fw-semibold d-block mb-1">TỔNG BÃI GIỮ XE</span>
                                    <h3 className="fw-extrabold text-dark mb-0">{parkingLots.length}</h3>
                                </div>
                                <div className="p-3 bg-primary-subtle text-primary rounded-4 fs-3">
                                    <i className="bi bi-building"></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 bg-white">
                            <Card.Body className="p-4 d-flex justify-content-between align-items-center">
                                <div>
                                    <span className="text-secondary small fw-semibold d-block mb-1">TỔNG SỨC CHỨA</span>
                                    <h3 className="fw-extrabold text-dark mb-0">{totalCapacity}</h3>
                                </div>
                                <div className="p-3 bg-success-subtle text-success rounded-4 fs-3">
                                    <i className="bi bi-pie-chart-fill"></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 bg-white">
                            <Card.Body className="p-4 d-flex justify-content-between align-items-center">
                                <div>
                                    <span className="text-secondary small fw-semibold d-block mb-1">XE ĐANG ĐỖ</span>
                                    <h3 className="fw-extrabold text-dark mb-0">{totalOccupied}</h3>
                                </div>
                                <div className="p-3 bg-warning-subtle text-warning rounded-4 fs-3">
                                    <i className="bi bi-car-front-fill"></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={3} sm={6}>
                        <Card className="border-0 shadow-sm rounded-4 bg-white">
                            <Card.Body className="p-4 d-flex justify-content-between align-items-center">
                                <div>
                                    <span className="text-secondary small fw-semibold d-block mb-1">VỊ TRÍ CÒN TRỐNG</span>
                                    <h3 className="fw-extrabold text-success mb-0">{availableSlots > 0 ? availableSlots : 0}</h3>
                                </div>
                                <div className="p-3 bg-info-subtle text-info rounded-4 fs-3">
                                    <i className="bi bi-check-circle-fill"></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Filter & Search Bar */}
                <Card className="border-0 shadow-sm rounded-4 bg-white mb-4">
                    <Card.Body className="p-3">
                        <Row className="g-3 align-items-center">
                            <Col md={6}>
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-0">
                                        <i className="bi bi-search text-secondary"></i>
                                    </span>
                                    <Form.Control
                                        type="text"
                                        placeholder="Tìm kiếm bãi xe theo tên hoặc vị trí..."
                                        className="bg-light border-0 py-2"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Parking Lot Grid */}
                <Row className="g-4">
                    {loading ? (
                        <Col xs={12} className="text-center py-5">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p className="text-muted mt-2">Đang tải thông tin bãi giữ xe...</p>
                        </Col>
                    ) : filteredLots.length === 0 ? (
                        <Col xs={12} className="text-center py-5">
                            <i className="bi bi-inbox text-secondary display-4 d-block mb-2"></i>
                            <p className="text-muted">Không tìm thấy bãi giữ xe phù hợp.</p>
                        </Col>
                    ) : (
                        filteredLots.map((lot, index) => {
                            const cap = parseInt(lot.capacity || lot.totalSpot || 100, 10);
                            const occ = parseInt(lot.currentVehicles || lot.occupiedSpot || 0, 10);
                            const rate = cap > 0 ? Math.round((occ / cap) * 100) : 0;
                            return (
                                <Col lg={4} md={6} key={index}>
                                    <Card className="border-0 shadow-sm rounded-4 bg-white h-100 parking-lot-card">
                                        <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                                            <h6 className="fw-bold text-dark mb-0">
                                                <i className="bi bi-geo-alt-fill text-danger me-2"></i>
                                                {lot.parkingLotName || lot.name || `Khu bãi ${index + 1}`}
                                            </h6>
                                            <Badge bg={rate >= 90 ? "danger" : rate >= 60 ? "warning" : "success"} className="rounded-pill px-3">
                                                {rate >= 90 ? "Đầy chỗ" : "Đang mở"}
                                            </Badge>
                                        </Card.Header>
                                        <Card.Body className="p-4">
                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between small text-secondary mb-1">
                                                    <span>Tỷ lệ lấp đầy:</span>
                                                    <span className="fw-bold text-dark">{rate}% ({occ}/{cap} xe)</span>
                                                </div>
                                                <ProgressBar
                                                    now={rate}
                                                    variant={rate >= 90 ? "danger" : rate >= 60 ? "warning" : "primary"}
                                                    style={{ height: "10px" }}
                                                    className="rounded-pill"
                                                />
                                            </div>

                                            <div className="bg-light p-3 rounded-3 mb-3 small">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Vị trí bãi:</span>
                                                    <span className="fw-semibold text-dark">{lot.location || "Đại học Công nghiệp TP.HCM"}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Số chỗ trống:</span>
                                                    <span className="fw-bold text-success">{cap - occ > 0 ? cap - occ : 0} vị trí</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">Trạng thái rào chắn:</span>
                                                    <span className="fw-semibold text-primary">Barie Tự Động</span>
                                                </div>
                                            </div>

                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="w-100 rounded-2 fw-semibold"
                                                    onClick={() => handleOpenEdit(lot)}
                                                >
                                                    <i className="bi bi-pencil-square me-1"></i> Chỉnh sửa
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            );
                        })
                    )}
                </Row>
            </Container>

            {/* Modal Edit / Add Parking Lot */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered className="modern-user-detail-modal">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold fs-6 text-dark d-flex align-items-center gap-2">
                        <i className={selectedLot ? "bi bi-pencil-square text-primary fs-5" : "bi bi-plus-circle-fill text-primary fs-5"}></i>
                        <span>{selectedLot ? "Cập Nhật Khu Vực Bãi Xe" : "Thêm Khu Vực Bãi Xe Mới"}</span>
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveLot}>
                    <Modal.Body className="p-4">
                        <Form.Group className="mb-3">
                            <Form.Label className="small text-secondary fw-semibold d-flex align-items-center gap-2">
                                <i className="bi bi-tag-fill text-primary"></i> Tên khu vực bãi xe *
                            </Form.Label>
                            <Form.Control
                                type="text"
                                className="fw-bold text-dark"
                                defaultValue={selectedLot?.parkingLotName || selectedLot?.name || ""}
                                placeholder="VD: BL, VL, VR, GL..."
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="small text-secondary fw-semibold d-flex align-items-center gap-2">
                                <i className="bi bi-speedometer2 text-primary"></i> Sức chứa tối đa (xe) *
                            </Form.Label>
                            <Form.Control
                                type="number"
                                className="fw-semibold"
                                defaultValue={selectedLot?.capacity || selectedLot?.totalSpot || 500}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="small text-secondary fw-semibold d-flex align-items-center gap-2">
                                <i className="bi bi-geo-alt-fill text-primary"></i> Vị trí mô tả *
                            </Form.Label>
                            <Form.Control
                                type="text"
                                defaultValue={selectedLot ? (selectedLot.location || selectedLot.description || "") : ""}
                                placeholder="VD: Tòa nhà V - Cổng chính"
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="bg-light">
                        <Button variant="secondary" className="rounded-2 px-3 fw-medium" onClick={() => setShowModal(false)}>
                            <i className="bi bi-x-lg me-1"></i> Hủy
                        </Button>
                        <Button variant="primary" type="submit" className="rounded-2 px-4 fw-bold shadow-sm">
                            <i className="bi bi-check-lg me-1"></i> Lưu thông tin
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default AdminParkingLot;
